import { useEffect, useMemo, useRef, useState } from 'react';
import { useFilters } from '../../../../../../hooks/useFilters';
import type { Filter } from '../../../../../../stores/filters';

export type URLParamFilterConfig = Record<string, { label: string; filterName: string; field: string }>;

interface UseCustomUrlParamFilterProps {
  filterConfig: URLParamFilterConfig;
  onChange: (filter: Filter) => void;
}

export function useCustomUrlParamFilter({ filterConfig, onChange }: UseCustomUrlParamFilterProps) {
  const [isParamsControlEnabled, setIsParamsControlEnabled] = useState(false);

  const { filters } = useFilters();

  const filtersList = useMemo(() => {
    return filters ? (Object.values(filters) as Array<Filter>) : [];
  }, [filters]);

  const relevantFiltersEnabled = useMemo(() => {
    const filterNames = new Set(Object.values(filterConfig).map(({ filterName }) => filterName));
    return filtersList.filter((f) => filterNames.has(f.filterName)).some((f) => f.enabled && f.expression !== '');
  }, [filtersList, filterConfig]);

  const prevRelevantFiltersEnabled = useRef(relevantFiltersEnabled);

  const getInitialDisplayedParams = () => {
    const initialParams = Object.entries(filterConfig)
      .filter(([_, { filterName }]) =>
        filtersList.find((f) => f.filterName === filterName && f.enabled && f.expression !== ''),
      )
      .map(([key]) => key);
    return initialParams.length ? initialParams : ['param1'];
  };

  const [paramsDisplayed, setParamsDisplayed] = useState<string[]>(['param1']);

  const paramsNotDisplayed = useMemo(() => {
    return Object.keys(filterConfig).filter((param) => !paramsDisplayed.includes(param));
  }, [paramsDisplayed, filterConfig]);

  const displayNewParam = () => {
    if (!paramsNotDisplayed.length) return;
    setParamsDisplayed((prev) => {
      if (prev.includes(paramsNotDisplayed[0])) return prev;
      return [...prev, paramsNotDisplayed[0]];
    });
  };

  const removeParam = (param: string) => {
    setParamsDisplayed((prev) => {
      if (prev.includes(param)) {
        const { filterName } = filterConfig[param];
        onChange({
          filterName,
          enabled: false,
          expression: '',
        });
        return prev.filter((p) => p !== param);
      }
      return prev;
    });
  };

  const handleCheckboxChange = (checked: boolean) => {
    setIsParamsControlEnabled(checked);

    if (checked) {
      setParamsDisplayed(getInitialDisplayedParams());
    } else {
      if (paramsDisplayed.length > 0) {
        paramsDisplayed.forEach((param) => {
          const { filterName } = filterConfig[param];
          onChange({
            filterName,
            enabled: false,
            expression: '',
          });
        });
      }
      setParamsDisplayed([]);
    }
  };

  useEffect(() => {
    const hasEnabledFilter = Object.values(filterConfig).some(({ filterName }) =>
      filtersList.some((filter) => filter.filterName === filterName && filter.enabled && filter.expression !== ''),
    );

    setIsParamsControlEnabled(hasEnabledFilter);

    if (hasEnabledFilter) {
      setParamsDisplayed(getInitialDisplayedParams());
    }
  }, []);

  useEffect(() => {
    const wasEnabled = prevRelevantFiltersEnabled.current;
    const isNowEnabled = relevantFiltersEnabled;

    prevRelevantFiltersEnabled.current = isNowEnabled;

    if (wasEnabled && !isNowEnabled && isParamsControlEnabled) {
      setIsParamsControlEnabled(false);
      setParamsDisplayed([]);
    }
  }, [relevantFiltersEnabled, isParamsControlEnabled]);

  useEffect(() => {
    if (paramsDisplayed.length === 0 && isParamsControlEnabled) {
      setIsParamsControlEnabled(false);
    }
  }, [paramsDisplayed, isParamsControlEnabled]);

  return {
    isParamsControlEnabled,
    paramsDisplayed,
    paramsNotDisplayed,
    displayNewParam,
    removeParam,
    handleCheckboxChange,
    setParamsDisplayed,
  };
}
